import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Trash2, Send, CalendarPlus, RotateCcw, Monitor, Lock, Loader2, FileDown } from "lucide-react";

export const MONTHS = ["GENNAIO", "FEBBRAIO", "MARZO", "APRILE", "MAGGIO", "GIUGNO", "LUGLIO", "AGOSTO", "SETTEMBRE", "OTTOBRE", "NOVEMBRE", "DICEMBRE"];
const pad = (n) => String(n).padStart(2, "0");
const num = (v) => {
  let s = String(v ?? "").trim().replace(/\s/g, "");
  s = s.includes(",") || !/\.\d{1,2}$/.test(s) ? s.replace(/\./g, "").replace(",", ".") : s.replace(/\.(?=.*\.)/g, "");
  return parseFloat(s) || 0;
};
const eur = (v) => v.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const short = (t) => (t ? t.replace(/^0(\d)/, "$1").replace(/:00$/, "") : "");

export const dayHours = (txt) => {
  let tot = 0;
  for (const m of String(txt || "").matchAll(/(\d{1,2})(?:[:.](\d{2}))?\s*-\s*(\d{1,2})(?:[:.](\d{2}))?/g)) {
    let d = (+m[3] * 60 + +(m[4] || 0)) - (+m[1] * 60 + +(m[2] || 0));
    if (d < 0) d += 1440;
    tot += d;
  }
  return tot / 60;
};

const dateLabel = (dates) => {
  const ds = dates.map((d) => +d.slice(8, 10));
  const mm = dates[0].slice(5, 7);
  return `${ds.length > 1 ? `${ds[0]}-${ds[ds.length - 1]}` : ds[0]}/${mm}`;
};

export const rowHours = (r) => (r.times?.length ? r.times.reduce((a, t) => a + dayHours(t.time), 0) : dayHours(r.time) * (r.days || 1));
const dayShort = (d) => `${+d.slice(8, 10)}/${d.slice(5, 7)}`;

function prefill(requests, reports, ym) {
  const conf = requests.filter((r) => r.status === "confermato" && r.date?.startsWith(ym)).sort((a, b) => a.date.localeCompare(b.date));
  const rows = [];
  for (const r of conf) {
    if (rows.some((x) => x.event_id === r.event_id && x.dates[0] === r.date)) continue;
    rows.push({ event_id: r.event_id, dates: [r.date], activity: r.event_name || "Evento", reqs: [] });
  }
  return rows.map((row, i) => {
    const rep = reports.reports.find((x) => x.event_id === row.event_id);
    const timeOf = (date) => {
      const slots = conf.filter((r) => r.event_id === row.event_id && r.date === date).sort((a, b) => (a.slot_start || "").localeCompare(b.slot_start || ""));
      const day = rep?.days?.find((d) => d.date === date);
      return (day?.start ? [`${short(day.start)}-${short(day.end)}`] : slots.filter((s) => s.slot_start).map((s) => `${short(s.slot_start)}-${short(s.slot_end)}`)).join(" ");
    };
    const firstRow = rows.findIndex((x) => x.event_id === row.event_id) === i;
    return { key: `c-${row.event_id}-${row.dates[0]}`, date: dateLabel(row.dates), days: row.dates.length, activity: row.activity, time: row.dates.length > 1 ? "" : timeOf(row.dates[0]),
      times: row.dates.length > 1 ? row.dates.map((d) => ({ date: d, time: timeOf(d) })) : [],
      pay: "", expenses: firstRow && rep?.expenses_total ? String(rep.expenses_total).replace(".", ",") : "", notes: "", added: false };
  });
}

const cell = "border-b border-slate-800 px-3 py-2.5 align-middle";
const inp = "w-full bg-slate-800/60 border border-slate-700/70 outline-none focus:border-cyan-500/60 focus:bg-slate-800 rounded-md px-2 py-1.5 text-center text-slate-100 placeholder:text-slate-600 transition-colors disabled:bg-transparent disabled:border-transparent disabled:placeholder:text-transparent";

function SheetRow({ row, onChange, onRemove, readOnly }) {
  const set = (k) => (e) => onChange({ ...row, [k]: e.target.value });
  const h = rowHours(row);
  const setDay = (date) => (e) => onChange({ ...row, times: row.times.map((t) => (t.date === date ? { ...t, time: e.target.value } : t)) });
  return (
    <tr data-testid={`ms-row-${row.key}`} className={`transition-colors ${row.added ? "bg-fuchsia-500/5 hover:bg-fuchsia-500/10" : "hover:bg-slate-800/40"}`}>
      <td className={`${cell} font-bold text-center`}>
        {row.added ? <input data-testid={`ms-date-${row.key}`} value={row.date} onChange={set("date")} placeholder="es. 15/10" className={`${inp} font-bold`} /> : row.date}
        {row.added && <div className="text-[9px] font-bold uppercase tracking-wider text-fuchsia-300 mt-1">aggiunta</div>}
      </td>
      <td className={`${cell} text-center text-slate-200`}>{row.added ? <input data-testid={`ms-activity-${row.key}`} value={row.activity} onChange={set("activity")} placeholder="Attività" className={inp} /> : row.activity}</td>
      <td className={cell}>{row.times?.length ? (
        <div className="space-y-1" data-testid={`ms-times-${row.key}`}>
          {row.times.map((t) => (
            <div key={t.date} className="flex items-center gap-1.5">
              <span className="text-[11px] font-mono text-slate-400 w-9 shrink-0 text-right">{dayShort(t.date)}</span>
              <input data-testid={`ms-time-${row.key}-${t.date}`} value={t.time} onChange={setDay(t.date)} placeholder="9-13 14-18" className={`${inp} py-1`} />
            </div>
          ))}
        </div>
      ) : <input data-testid={`ms-time-${row.key}`} value={row.time} onChange={set("time")} placeholder="es. 9-13 14-18" className={inp} />}</td>
      <td className={`${cell} text-center whitespace-nowrap font-mono text-cyan-300`} data-testid={`ms-hours-${row.key}`}>{h ? `${eur(h)} ore${row.days > 1 && !row.times?.length ? " gg" : ""}` : <span className="text-slate-600">–</span>}</td>
      <td className={cell}><input data-testid={`ms-pay-${row.key}`} inputMode="decimal" value={row.pay} onChange={set("pay")} className={inp} /></td>
      <td className={cell}><input data-testid={`ms-exp-${row.key}`} inputMode="decimal" value={row.expenses} onChange={set("expenses")} className={inp} /></td>
      <td className={cell}><input data-testid={`ms-notes-${row.key}`} value={row.notes} onChange={set("notes")} className={`${inp} text-left`} /></td>
      <td className="pl-2 w-6">{row.added && !readOnly && <button data-testid={`ms-remove-${row.key}`} onClick={onRemove} title="Elimina riga"><Trash2 className="w-4 h-4 text-rose-400" /></button>}</td>
    </tr>
  );
}

export function Sheet({ rows, y, m, update, readOnly = false }) {
  const tot = rows.reduce((a, r) => ({ h: a.h + rowHours(r), pay: a.pay + num(r.pay), exp: a.exp + num(r.expenses) }), { h: 0, pay: 0, exp: 0 });
  const th = "px-3 py-3 font-medium text-center uppercase text-[11px] tracking-wider text-slate-400 border-b border-slate-700";
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900 text-sm" data-testid="ms-sheet">
      <div className="flex items-center gap-2 px-4 pt-4">
        <span data-testid="ms-year" className="rounded-md bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 font-bold px-2.5 py-1 text-xs">{y}</span>
        <span data-testid="ms-month" className="rounded-md bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 font-bold px-2.5 py-1 text-xs tracking-wider">{MONTHS[m - 1]}</span>
      </div>
      <fieldset disabled={readOnly} className="contents">
      <table className="w-full min-w-[960px] border-collapse mt-2">
        <thead className="bg-slate-900">
          <tr>
            <th className={`${th} w-44`}>Data</th><th className={`${th} w-56`}>Attività</th><th className={`${th} w-52`}>Orario</th><th className={`${th} w-28`}>Tot ore</th>
            <th className={`${th} w-36 whitespace-nowrap`}>Paga (IVA escl.)</th><th className={`${th} w-32`}>Rimborsi spese</th><th className={th}>Note</th><th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className={`${cell} text-center text-slate-500 py-6`}>Nessuna conferma in questo mese: aggiungi tu le giornate lavorate.</td></tr>}
          {rows.map((r) => <SheetRow key={r.key} row={r} readOnly={readOnly} onChange={(n) => update(rows.map((x) => (x.key === r.key ? n : x)))} onRemove={() => update(rows.filter((x) => x.key !== r.key))} />)}
        </tbody>
        <tfoot>
          <tr className="font-bold bg-slate-800/60" data-testid="ms-totals">
            <td className="px-3 py-3 text-center uppercase tracking-wider text-xs text-slate-300" colSpan={3}>Totale</td>
            <td className="px-3 py-3 text-center font-mono text-cyan-300" data-testid="ms-total-hours">{eur(tot.h)} ore</td>
            <td className="px-3 py-3 text-center text-emerald-300 text-base" data-testid="ms-total-pay">€ {eur(tot.pay)}</td>
            <td className="px-3 py-3 text-center text-amber-300" data-testid="ms-total-exp">€ {eur(tot.exp)}</td>
            <td /><td />
          </tr>
        </tfoot>
      </table>
      </fieldset>
    </div>
  );
}

export const downloadPdf = async (url, name) => {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = u; a.download = name;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
  } catch { toast.error("PDF non disponibile: compila almeno una riga"); }
};

export function MonthlyStatement({ requests, reports }) {
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const base = useMemo(() => prefill(requests, reports, ym), [requests, reports, ym]);
  const [saved, setSaved] = useState(undefined);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setSaved(undefined); setDirty(false);
    api.get(`/statements/mine/${ym}`).then((r) => setSaved(r.data)).catch(() => setSaved(null));
  }, [ym]);
  useEffect(() => {
    if (saved === undefined || dirty) return;
    if (!saved?.rows || saved.locked) { setRows(saved?.rows || base); return; }
    const old = Object.fromEntries(saved.rows.filter((r) => !r.added).map((r) => [r.key, r]));
    const merged = base.map((b) => {
      const o = old[b.key];
      const d = b.key.slice(-10);
      const legacy = saved.rows.find((r) => r.times?.some((t) => t.date === d))?.times.find((t) => t.date === d)?.time;
      if (!o) return legacy ? { ...b, time: legacy } : b;
      return { ...b, time: (o.times?.length ? legacy : o.time) || b.time, times: [], pay: o.pay, expenses: o.expenses || b.expenses, notes: o.notes };
    });
    setRows([...merged, ...saved.rows.filter((r) => r.added)]);
  }, [saved, base, dirty]);
  const locked = !!saved?.locked;
  const update = (next) => {
    setRows(next); setDirty(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => api.put(`/statements/mine/${ym}`, { rows: next }).catch(() => toast.error("Bozza non salvata")), 800);
  };
  const pdf = async () => {
    clearTimeout(timer.current);
    if (!locked) { try { await api.put(`/statements/mine/${ym}`, { rows }); } catch { toast.error("Bozza non salvata"); return; } }
    downloadPdf(`/statements/mine/${ym}/pdf`, `resoconto-${ym}.pdf`);
  };
  const send = async () => {
    if (!rows.length) { toast.error("Il resoconto è vuoto"); return; }
    if (!window.confirm("Inviare il resoconto al manager? Dopo l'invio non potrai più modificarlo.")) return;
    clearTimeout(timer.current);
    setBusy(true);
    try { const r = await api.post(`/statements/mine/${ym}/submit`, { rows }); setSaved(r.data); setDirty(false); toast.success("Resoconto inviato al manager"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const shift = (d) => { const [yy, mm] = ym.split("-").map(Number); const x = new Date(yy, mm - 1 + d, 1); setYm(`${x.getFullYear()}-${pad(x.getMonth() + 1)}`); };
  const [y, m] = ym.split("-").map(Number);
  return (
    <div className="space-y-4 animate-fade-up" data-testid="monthly-statement">
      {locked ? (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200 flex items-center gap-2" data-testid="ms-locked-banner">
          <Lock className="w-3.5 h-3.5" /> Inviato al manager il {new Date(saved.submitted_at).toLocaleString("it-IT", { timeZone: "Europe/Rome", dateStyle: "short", timeStyle: "short" })}: è bloccato. Per correggerlo chiedi al manager di sbloccarlo.</div>
      ) : saved?.submitted_at ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="ms-unlocked-banner">Il manager ha sbloccato il resoconto: correggilo e invialo di nuovo.</div>
      ) : null}
      <p className="md:hidden flex items-center gap-2 text-xs text-slate-400" data-testid="ms-desktop-hint"><Monitor className="w-4 h-4" /> Il resoconto si compila meglio dal computer.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="icon" variant="ghost" data-testid="ms-prev-month" onClick={() => shift(-1)}><ChevronLeft className="w-5 h-5" /></Button>
        <h1 className="font-display text-xl font-bold">Resoconto {MONTHS[m - 1].toLowerCase()} {y} <span className="text-sm font-normal text-slate-500">· dal 1 al {new Date(y, m, 0).getDate()}</span></h1>
        <Button size="icon" variant="ghost" data-testid="ms-next-month" onClick={() => shift(1)}><ChevronRight className="w-5 h-5" /></Button>
        {saved !== undefined && rows.length > 0 && (
          <Button variant="outline" data-testid="ms-pdf" onClick={pdf} className={`border-slate-700 ${locked ? "ml-auto" : ""}`}><FileDown className="w-4 h-4 mr-1.5" /> Scarica PDF</Button>
        )}
        {!locked && saved !== undefined && (
          <div className="ml-auto flex gap-2">
            <Button variant="outline" data-testid="ms-add-day" onClick={() => update([...rows, { key: `add-${Date.now()}`, date: "", days: 1, activity: "", time: "", pay: "", expenses: "", notes: "", added: true }])}
              className="border-fuchsia-500/40 text-fuchsia-200"><CalendarPlus className="w-4 h-4 mr-1.5" /> Aggiungi riga</Button>
            <Button variant="ghost" data-testid="ms-reset" onClick={() => { if (window.confirm("Ripartire dalle conferme del mese? Le modifiche fatte verranno perse.")) update(base); }} className="text-slate-400"><RotateCcw className="w-4 h-4 mr-1" /> Ricomincia</Button>
          </div>
        )}
      </div>
      {saved === undefined ? <p className="text-sm text-slate-500">Caricamento…</p> : <Sheet rows={rows} y={y} m={m} update={update} readOnly={locked} />}
      {!locked && <p className="text-xs text-slate-500">Scrivi l'orario come nel foglio (es. «9-13 14-18»): le ore si calcolano da sole. Ogni giorno ha la sua riga con orario, paga e rimborsi. Le righe viola le hai aggiunte tu. La bozza si salva da sola.</p>}
      {!locked && saved !== undefined && (
        <Button disabled={busy} onClick={send} data-testid="ms-send" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Invia resoconto al manager</>}</Button>
      )}
    </div>
  );
}
